import { useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { withNamespaces } from "react-i18next";
import { Alert, Badge, Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { 
    EMPTY_LIST,
    INTERNATIONAL_DATE_TIME_FORMAT
} from "src/common/constants";
import {
    OrdersStatuses
} from "src/common/constants/Common";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import OrdersService from "src/apis/OrdersService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import DateUtils from "src/services/utils/DateUtils";
import { Link } from "react-router-dom";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const Orders = ({ t }) => {
    const dateUtils = new DateUtils();

    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });

    const fetchOrdersQuery = useQuery({
        queryKey: [
            "app-fetch-orders-list",
            "wb",
            filters
        ],
        queryFn: async () => {
            const ordersService = OrdersService.getInstance();

            return await ordersService.fetchOrders(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: isFeatureEnabledForActiveModule('subscription_report'),
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('Failed to load orders.'), {
                type: "error"
            });
        }
    });

    if (!isFeatureEnabledForActiveModule('subscription_report')) {
        return (
            <div className="page-content">
                <Alert
                    color="warning"
                >
                    <p className="mb-0">
                        {t("Please upgrade your plan to access this feature")}.
                    </p>
                </Alert>
            </div>
        )
    }

    const columns = [
        {
            dataField: "number",
            text: t("Order ID"),
            sort: false,
            formatter: (cellContent) => {
                return (
                    <Link
                        to={`/admin/administration/orders/${cellContent}`}
                    >
                        {cellContent}
                    </Link>
                )
            }
        },
        {
            dataField: "status",
            text: t("Status"),
            sort: false,
            formatter: (cellContent) => {
                const status = OrdersStatuses[cellContent];
                
                return (
                    <Badge
                        pill
                        style={{
                            backgroundColor: status ? status.color : "#E0E0E0"
                        }}
                    >
                        {t(status ? status.title : cellContent)}
                    </Badge>
                )
            }
        },
        {
            dataField: "date_created",
            text: t("Created At"),
            sort: false,
            formatter: (cellContent) => {
                return dateUtils.convertDateToDate(
                    cellContent,
                    INTERNATIONAL_DATE_TIME_FORMAT
                )
            }
        },
        {
            dataField: "date_paid",
            text: t("Paid At"),
            sort: false,
            formatter: (cellContent) => {
                return dateUtils.convertDateToDate(
                    cellContent,
                    INTERNATIONAL_DATE_TIME_FORMAT
                )
            }
        },
        {
            dataField: "total",
            text: t("Total"),
            sort: false,
            formatter: (cellContent, { currency }) => {
                return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency,
                    maximumFractionDigits: 2
                }).format(cellContent)
            }
        },
        {
            dataField: "number",
            text: t("Invoice No."),
            sort: false,
            formatter: (cellContent) => cellContent || "-"  
        },
        {
            dataField: "actions",
            text: t("Action"),
            sort: false,
            formatter: (cell, { id }) => {
                return (
                    <div className="actions">
                        <Link 
                            to={`/admin/administration/orders/${id}`}
                        >
                            <i className='ri-eye-line font-size-16'></i>
                        </Link>
                    </div>
                )
            }
        }
    ];

    const NoDataIndication = () => (
        (fetchOrdersQuery.isFetched && !fetchOrdersQuery.data?.orders?.length) ? <div className="alert m-0" role="alert">
            <p style={{
                textAlign: "center",
                marginBottom: 0
            }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    );

    const {
        orders = [],
        total = 0
    } = fetchOrdersQuery.data || {};

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs
                    title={t('Orders')}
                    breadcrumbItems={[
                        { title: "SIP", link: "/" },
                        { title: t("Orders"), link: "#" },
                    ]}
                />

                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody className="pt-0">
                                <PaginationProvider
                                    pagination={
                                        paginationFactory({
                                            custom: true,
                                            page: filters.pageIndex,
                                            sizePerPage: filters.pageSize,
                                            totalSize: total,
                                            withFirstAndLast: true,
                                            alwaysShowAllBtns: true,
                                            prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                            nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                            prePageTitle: t('Pre page'),
                                            firstPageTitle: t('First page'),
                                            lastPageTitle: t('Last page'),
                                            showTotal: false,
                                            paginationTotalRenderer: (from, to, size) => {
                                                return (
                                                    <span className="react-bootstrap-table-pagination-total">
                                                        {`${t('Total Items')} ${size}`}
                                                    </span>
                                                )
                                            },
                                            hideSizePerPage: false,
                                            sizePerPageList: [
                                                {
                                                    text: '25',
                                                    value: 25
                                                },
                                                {
                                                    text: '50',
                                                    value: 50
                                                }
                                            ]
                                        })
                                    }>
                                    {
                                        ({
                                            paginationProps,
                                            paginationTableProps
                                        }) => (
                                            <>
                                                <Row>
                                                    <Col sm="12">
                                                        <BootstrapTable
                                                            remote={{
                                                                pagination: true,
                                                                filter: false,
                                                                sort: true,
                                                                cellEdit: false,
                                                                search: false
                                                            }}
                                                            loading={false}
                                                            overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                            onTableChange={() => console.log("Table changed")}
                                                            defaultSorted={[]}
                                                            keyField={"id"}
                                                            striped={false}
                                                            responsive={true}
                                                            bordered={false}
                                                            data={orders}
                                                            columns={columns}
                                                            wrapperClasses="table-responsive"
                                                            classes={"table"}
                                                            headerWrapperClasses={"thead-light"}
                                                            noDataIndication={() => <NoDataIndication />}
                                                            {...paginationTableProps}
                                                        />
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col sm="12">
                                                        <div className="tpdd-pagination-style-1 justify-content-between">
                                                            <PaginationTotalStandalone
                                                                {...paginationProps}
                                                            />

                                                            <div>
                                                                <PaginationListStandalone
                                                                    {...paginationProps}
                                                                />

                                                                <SizePerPageDropdownStandalone
                                                                    {...paginationProps}
                                                                />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )
                                    }

                                </PaginationProvider>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default withNamespaces()(
    Orders
);